import React, { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AlertCircle } from 'lucide-react';
import SearchInput from './SearchInput';
import Button from './Button';
import CloseButton from './CloseButton';
import TierBadge from './TierBadge';
import AssessorSelector from './AssessorSelector';
import ModalLoadingOverlay from './ModalLoadingOverlay';
import Message from './Message';
import { Textarea } from '@components/common';
import type { AssessorOption } from './AssessorSelector';
import { userService } from '@services';
import { formatPersonName } from '@utils/stringUtils';
import { ScheduleItem, ApiError } from '@app-types';
import { tierToVariant } from '@utils/badgeMappers';
import styles from './AssessorAssignmentModal.module.css';

export interface AssessorAssignmentModalProps {
  scheduleItem: ScheduleItem;
  isReassignment?: boolean; // For meetings that already have assessor
  onClose: () => void;
  onAssigned: (assessorId: number, reason: string) => Promise<void>;
  dataTestId?: string;
}

const AssessorAssignmentModal: React.FC<AssessorAssignmentModalProps> = ({
  scheduleItem,
  isReassignment = false,
  onClose,
  onAssigned,
  dataTestId = 'assessor-assignment'
}) => {
  const currentAssessorId = scheduleItem?.assessor?.id || 0;
  const currentAssessorName = scheduleItem?.assessor?.name || '';
  const contracts = scheduleItem?.contracts || [];

  const [selectedAssessorId, setSelectedAssessorId] = useState<number>(currentAssessorId);
  const [reason, setReason] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Fetch all assessors
  const { data: assessors, isLoading: loadingAssessors } = useQuery({
    queryKey: ['users', 'Assessor'],
    queryFn: () => userService.getUsers('Assessor')
  });

  // Fetch workload information
  const { data: workloadData, isLoading: loadingWorkload } = useQuery({
    queryKey: ['assessors-workload'],
    queryFn: () => userService.getAssessorsWithWorkload()
  });

  // Combine assessors with workload data
  const assessorsWithWorkload = useMemo<AssessorOption[]>(() => {
    if (!assessors) return [];

    // Create a map of workload data by assessorId for quick lookup
    const workloadMap = new Map(workloadData?.map((w) => [w.assessorId, w]) || []);

    return assessors.map((assessor) => ({
      assessorId: assessor.id,
      assessorName: formatPersonName(assessor),
      email: assessor.email,
      totalMeetings: workloadMap.get(assessor.id)?.totalMeetings || 0,
      scheduledMeetings: workloadMap.get(assessor.id)?.scheduledMeetings || 0,
      completedMeetings: workloadMap.get(assessor.id)?.completedMeetings || 0
    }));
  }, [assessors, workloadData]);

  // Filter assessors based on search term
  const filteredAssessors = useMemo(() => {
    if (!assessorsWithWorkload || assessorsWithWorkload.length === 0) return [];

    let result = [...assessorsWithWorkload];

    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      result = result.filter(
        (a) => a.assessorName.toLowerCase().includes(searchLower) || a.email.toLowerCase().includes(searchLower)
      );
    }

    // Sort by workload (least busy first) to help with load balancing
    return result.sort((a, b) => (a.totalMeetings || 0) - (b.totalMeetings || 0));
  }, [assessorsWithWorkload, searchTerm]);

  const isLoading = loadingAssessors || loadingWorkload;

  const handleApply = async () => {
    if (!selectedAssessorId) {
      setError('Please select an assessor');
      return;
    }

    // Only require reason for meeting reassignments (not scheduler assignments)
    if (isReassignment && !reason.trim()) {
      setError('Please provide a reason for reassignment');
      return;
    }

    setError(null);
    setIsSubmitting(true);

    try {
      await onAssigned(selectedAssessorId, reason);
      onClose();
    } catch (error) {
      const apiError = error as ApiError;
      const errorMessage = apiError.response?.data?.error || 'Failed to assign assessor';
      setError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const hasChanges = selectedAssessorId !== currentAssessorId || reason.trim().length > 0;

  return (
    <div className={styles.assessorModalOverlay} data-test-id={`${dataTestId}-modal`}>
      <div className={styles.assessorModal}>
        {/* Loading Overlay */}
        {isSubmitting && <ModalLoadingOverlay message="Processing request..." />}

        <div className={styles.assessorHeader}>
          <div className={styles.headerText}>
            <h3 data-test-id={`${dataTestId}-title`}>{isReassignment ? 'Reassign Assessor' : 'Assign Assessor'}</h3>
            <p className={styles.important}>
              Select an assessor for this meetings{contracts.length > 1 ? ' (group)' : ''}
            </p>
          </div>
          <CloseButton onClick={onClose} disabled={isSubmitting} />
        </div>

        <div className={styles.assessorContent}>
          {/* Schedule Item Info */}
          <div className={styles.scheduleItemInfo}>
            <h5>Schedule Item Details:</h5>
            <div className={styles.contractsList}>
              {scheduleItem.contracts.map((contract, index) => (
                <div key={contract.id} className={styles.contractItem}>
                  <span className={styles.contractNumber}>{contract.contract_number}</span>
                  <span className={styles.contractSupplier}>{contract.supplier_name}</span>
                  <TierBadge variant={tierToVariant(contract.tier)} className={styles.contractItemTier}>
                    {contract.tier}
                  </TierBadge>
                </div>
              ))}
            </div>
            <div className={styles.currentAssessor}>
              <strong>Current Assessor:</strong> {currentAssessorName || 'Not Assigned'}
            </div>
          </div>

          {/* Search Bar */}
          <SearchInput
            value={searchTerm}
            onChange={setSearchTerm}
            placeholder="Search assessors by name or email..."
            disabled={isSubmitting}
          />

          {/* Assessors List */}
          <AssessorSelector
            assessors={filteredAssessors}
            selectedAssessorId={selectedAssessorId}
            onSelectAssessor={setSelectedAssessorId}
            isLoading={isLoading}
            disabled={isSubmitting}
            showWorkload={true}
            layout="list"
          />

          {/* Reason Field - Only for meeting reassignments */}
          {isReassignment && (
            <div className={styles.reasonField}>
              <Textarea
                id="reason"
                label="Reason for Reassignment"
                required
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="e.g., Out sick next two weeks, Workload rebalancing, Industry expertise required..."
                rows={3}
                disabled={isSubmitting}
                dataTestId="reassignment-reason"
              />
            </div>
          )}

          {/* Status Messages */}
          {error && <Message variant="error" icon={<AlertCircle size={16} />}>{error}</Message>}
        </div>

        <div className={styles.assessorFooter}>
          <Button variant="ghost" onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>

          <Button variant="primary" onClick={handleApply} disabled={!hasChanges || isSubmitting}>
            {isSubmitting ? 'Assigning...' : 'Assign Assessor'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AssessorAssignmentModal;
